/**
 * Front-end half of the continuation-signal contract.
 *
 * The rule and the reasoning live in `backend/modules/agent_of_agents/
 * signals.py`; this is the same rule for the two moments Python cannot
 * reach:
 *
 *   1. The LIVE token stream. Tokens render as they arrive, long before the
 *      backend cleans the finished message at `ai_complete`, so an echo
 *      would be on screen for the length of the turn.
 *   2. Rows ALREADY stored with an echo in them. The backend now cleans at
 *      write time, but nothing rewrites history.
 *
 * Kept byte-identical to the Python constants; the parity is pinned by
 * tests/unit/agent_of_agents/test_signal_contract_parity.py, which reads
 * this file. (Same precedent as `_TOOLISH_SKILL_NAME` in the AoA routes.)
 */

/** Opens every injected signal. */
export const SIGNAL_MARKER = '[system signal — not from the user]';

/** Closes it — what makes an echo exactly removable rather than guessable. */
export const SIGNAL_TERMINATOR = '[/system signal]';

/** Cheap containment test; shorter than the marker on purpose, so rewording
 *  the text after "[system signal" still gets stripped. */
export const SIGNAL_PREFIX = '[system signal';

const ESC = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** A complete, terminated signal. Non-greedy so two signals in one turn are
 *  two spans, rather than one span swallowing the answer between them. */
const SIGNAL_SPAN = new RegExp(
  `${ESC(SIGNAL_PREFIX)}[\\s\\S]*?${ESC(SIGNAL_TERMINATOR)}`,
  'g',
);

/**
 * Remove signals the model copied into its own reply.
 *
 * Returns '' only when the text was nothing BUT signal. It never returns
 * less than the non-signal content.
 *
 * The first version split on blank lines and dropped any paragraph starting
 * with the marker, which silently destroyed real answers — nothing obliges
 * a model to leave a blank line between the instruction it is parroting and
 * its own reply, and `"[system signal ...] Do not greet again.\nHere is the
 * answer."` is ONE paragraph. Caught in review. Signals are terminated now,
 * so the span is exact; the paragraph rule survives only for rows stored
 * before the terminator existed, and only when it leaves something behind.
 */
export function stripEchoedSignals(text: string): string {
  if (!text || !text.includes(SIGNAL_PREFIX)) return text;

  // Cutting a span out from between two paragraphs leaves the blank line
  // before it AND the one after, so collapse the run rather than leaving a
  // visible gap where the instruction used to be.
  let cleaned = text.replace(SIGNAL_SPAN, '').replace(/\n{3,}/g, '\n\n').trim();

  if (cleaned.includes(SIGNAL_PREFIX)) {
    // Unterminated: either a legacy row, or a signal still mid-stream.
    const kept = cleaned
      .split(/\n\s*\n/)
      .filter((b) => !b.trimStart().startsWith(SIGNAL_PREFIX));
    const candidate = kept.join('\n\n').trim();
    // Only take it when something survives. Showing the instruction is bad;
    // blanking the user's answer is worse.
    if (candidate) cleaned = candidate;
  }

  return cleaned;
}
